import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { ClassSchedule, ExamSchedule, TeacherScheduleType } from '../types';
import { classSchedule, examSchedule } from '../constant';

interface AcademicScheduleContextType {
  localScheduleData: ClassSchedule[];
  setLocalScheduleData: React.Dispatch<React.SetStateAction<ClassSchedule[]>>;
  filteredClassSchedules: ClassSchedule[] | undefined;
  setFilteredClassSchedules: React.Dispatch<React.SetStateAction<ClassSchedule[] | undefined>>;
  setNewSchedule: React.Dispatch<React.SetStateAction<ClassSchedule>>;
  newSchedule: ClassSchedule;
  setShowAddClassSchedule: React.Dispatch<React.SetStateAction<boolean>>;
  showAddClassSchedule: boolean;
  setShowAddExamSchedule: React.Dispatch<React.SetStateAction<boolean>>;
  showAddExamSchedule: boolean;
  setNewExamSchedule: React.Dispatch<React.SetStateAction<ExamSchedule>>;
  newExamSchedule: ExamSchedule;
  localExamScheduleData: ExamSchedule[];
  setExamLocalScheduleData: React.Dispatch<React.SetStateAction<ExamSchedule[]>>;
  setFilteredExamSchedules: React.Dispatch<React.SetStateAction<ExamSchedule[] | undefined>>;
  filteredExamSchedules: ExamSchedule[] | undefined;
  setSelectedFilter: React.Dispatch<React.SetStateAction<number>>;
  selectedFilter: number;
  setSelectedAddSchedule: React.Dispatch<React.SetStateAction<number>>;
  selectedAddSchedule: number;
  newTeacherSchedule: TeacherScheduleType;
  setNewTeacherSchedule: React.Dispatch<React.SetStateAction<TeacherScheduleType>>;
  setFilteredTeacherSchedules: React.Dispatch<React.SetStateAction<TeacherScheduleType[] | undefined>>;
  filteredTeacherSchedules: TeacherScheduleType[] | undefined;
}

const AcademicScheduleContext = createContext<AcademicScheduleContextType | undefined>(undefined);

const AcademicScheduleProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [localScheduleData, setLocalScheduleData] = useState<ClassSchedule[]>(classSchedule);
  const [localExamScheduleData, setExamLocalScheduleData] = useState<ExamSchedule[]>(examSchedule);
  const [filteredClassSchedules, setFilteredClassSchedules] = useState<ClassSchedule[]>();
  const [filteredExamSchedules, setFilteredExamSchedules] = useState<ExamSchedule[]>();
  const [filteredTeacherSchedules, setFilteredTeacherSchedules] = useState<TeacherScheduleType[]>();
  const [showAddClassSchedule, setShowAddClassSchedule] = useState(false);
  const [showAddExamSchedule, setShowAddExamSchedule] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<number>(1);
  const [selectedAddSchedule, setSelectedAddSchedule] = useState<number>(1);
  const [newSchedule, setNewSchedule] = useState<ClassSchedule>({
    class: '',
    section: '',
    startDate: null,
    endDate: null,
    scheduleType: 'Class',
    scheduleStructure: [],
  });
  const [newExamSchedule, setNewExamSchedule] = useState<ExamSchedule>({
    class: '',
    section: '',
    examName: '',
    scheduleType: 'Exam',
    schedule: [],
    seatingPlan: [],
  });

  const [newTeacherSchedule, setNewTeacherSchedule] = useState<TeacherScheduleType>({} as TeacherScheduleType);

  return (
    <AcademicScheduleContext.Provider
      value={{
        localScheduleData,
        setLocalScheduleData,
        setFilteredClassSchedules,
        filteredClassSchedules,
        setNewSchedule,
        newSchedule,
        setShowAddClassSchedule,
        showAddClassSchedule,
        setShowAddExamSchedule,
        showAddExamSchedule,
        setNewExamSchedule,
        newExamSchedule,
        localExamScheduleData,
        setExamLocalScheduleData,
        setFilteredExamSchedules,
        filteredExamSchedules,
        setSelectedFilter,
        selectedFilter,
        setSelectedAddSchedule,
        selectedAddSchedule,
        setNewTeacherSchedule,
        newTeacherSchedule,
        setFilteredTeacherSchedules,
        filteredTeacherSchedules,
      }}
    >
      {children}
    </AcademicScheduleContext.Provider>
  );
};

export const useAcademicSchedule = (): AcademicScheduleContextType => {
  const context = useContext(AcademicScheduleContext);
  if (!context) {
    throw new Error('useAcademicSchedule must be used within an AcademicScheduleProvider');
  }
  return context;
};

export default AcademicScheduleProvider;
